/* Smart Flow Services - Glass Corporate Tower Architectural Model (js/building.js)
   Constructs a corporate glass skyscraper with:
   - Emissive / semi-transparent glass curtain wall facade
   - Steel mullions, vertical columns, and horizontal floor divider slabs
   - Vertical floor compartments matching stacked Y coordinates:
       * Ground / Street level Lobby: y: -6 to 10 (center y: 5.0)
       * Floor 1 (Server Farm / Hall): y: 10 to 22 (center y: 16)
       * Floor 2 (Boardroom & Projects): y: 22 to 34 (center y: 28)
       * Floor 3 (Penthouse / Executive Office): y: 34 to 46 (center y: 40)
       * Spire & Architectural Crown: y: 46 to 66 (center y: 56)
   - Neon accent edge trims, perimeter roof balustrades, glowing corner beacons
*/

import * as THREE from "three";

/** Current local hour in Israel (DST handled by the Intl timezone). */
function israelHour() {
  try {
    return Number(
      new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Jerusalem",
        hour: "2-digit",
        hour12: false,
      }).format(new Date()),
    );
  } catch (_) {
    const d = new Date();
    return (d.getUTCHours() + 3) % 24;
  }
}

function isDayInIsrael() {
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    if (params.get("night") === "1" || params.get("time") === "night")
      return false;
    if (params.get("day") === "1" || params.get("time") === "day") return true;
  }
  const h = israelHour();
  return h >= 6 && h < 19;
}

/**
 * Generates an ultra-clean, smooth architectural studio sky environment map.
 * Adapts between Day and Night according to Israel time.
 */
function makeCityEnvMap(THREE, isDay) {
  const c = document.createElement("canvas");
  c.width = 1024;
  c.height = 512;
  const g = c.getContext("2d");

  if (isDay) {
    // Crisp daylight reflection: Deep royal blue zenith -> Open azure -> Light sky horizon
    const grad = g.createLinearGradient(0, 0, 0, 512);
    grad.addColorStop(0.0, "#0a2e6b"); // Deep royal blue zenith
    grad.addColorStop(0.25, "#1b6ed8"); // Vivid day sky
    grad.addColorStop(0.5, "#4a9fed"); // Soft azure
    grad.addColorStop(0.68, "#8ec7f8"); // Bright horizon
    grad.addColorStop(0.85, "#cbd5e1"); // Neutral ground reflection
    grad.addColorStop(1.0, "#94a3b8");
    g.fillStyle = grad;
    g.fillRect(0, 0, 1024, 512);

    // Small, distant sun in the env map - used only for physical edge sheen,
    // not a washed-out white band across the whole tower.
    const sunSpot = g.createRadialGradient(380, 168, 0, 380, 168, 46);
    sunSpot.addColorStop(0.0, "rgba(255, 255, 255, 0.95)");
    sunSpot.addColorStop(0.35, "rgba(255, 248, 230, 0.45)");
    sunSpot.addColorStop(1.0, "rgba(255, 255, 255, 0.0)");
    g.fillStyle = sunSpot;
    g.fillRect(0, 0, 1024, 512);
  } else {
    // Smooth vertical studio twilight gradient (deep navy -> soft cyan horizon -> rich dark base)
    const grad = g.createLinearGradient(0, 0, 0, 512);
    grad.addColorStop(0.0, "#020712");
    grad.addColorStop(0.35, "#0b1c30");
    grad.addColorStop(0.48, "#1a3b5c");
    grad.addColorStop(0.52, "#13273e");
    grad.addColorStop(0.7, "#060d17");
    grad.addColorStop(1.0, "#010306");
    g.fillStyle = grad;
    g.fillRect(0, 0, 1024, 512);

    // Soft moon/city white-cyan gleam on glass
    const moonSpot = g.createRadialGradient(420, 210, 0, 420, 210, 140);
    moonSpot.addColorStop(0.0, "rgba(255, 255, 255, 0.75)");
    moonSpot.addColorStop(0.3, "rgba(102, 204, 240, 0.35)");
    moonSpot.addColorStop(1.0, "rgba(102, 204, 240, 0.0)");
    g.fillStyle = moonSpot;
    g.fillRect(0, 0, 1024, 512);
  }

  const tex = new THREE.CanvasTexture(c);
  tex.mapping = THREE.EquirectangularReflectionMapping;
  return tex;
}

/** Photographic sun-catch on a curtain-wall facade.
    Light lives INSIDE the window panes only - mullions stay dark and
    break the highlight the way real glass does. No sticker flare, no X. */
function makeFacadeSunCatchTexture(THREE, opts) {
  const cols = opts.cols;
  const rows = opts.rows;
  const width = opts.width;
  const height = opts.height;
  const hitU = opts.hitU;
  const hitV = opts.hitV;
  const strength = opts.strength == null ? 1 : opts.strength;
  const aspect = width / height;
  const CH = 1024;
  const CW = Math.max(512, Math.round(CH * aspect));
  const c = document.createElement("canvas");
  c.width = CW;
  c.height = CH;
  const g = c.getContext("2d");
  g.clearRect(0, 0, CW, CH);

  const mullionX = Math.max(5, (0.2 / width) * CW);
  const mullionY = Math.max(5, (0.2 / height) * CH);
  const cellW = CW / cols;
  const cellH = CH / rows;
  const insetX = mullionX * 0.55;
  const insetY = mullionY * 0.55;

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const u = (col + 0.5) / cols;
      const v = (row + 0.5) / rows;
      const du = (u - hitU) * 1.15;
      const dv = v - hitV;
      const dist = Math.sqrt(du * du + dv * dv);
      const falloff = Math.exp(-(dist * dist) / 0.085);
      const intensity = falloff * strength;
      if (intensity < 0.035) continue;

      const x = col * cellW + insetX;
      const y = row * cellH + insetY;
      const w = cellW - insetX * 2;
      const h = cellH - insetY * 2;
      const cx = x + w * (0.35 + (hitU - 0.5) * 0.25);
      const cy = y + h * (0.4 + (hitV - 0.5) * 0.2);
      const radius = Math.max(w, h) * (0.55 + intensity * 0.55);

      g.save();
      g.beginPath();
      if (g.roundRect) g.roundRect(x, y, w, h, 3);
      else g.rect(x, y, w, h);
      g.clip();

      // Cool glass sheen that stays inside the pane.
      const sheen = g.createLinearGradient(x, y, x, y + h);
      sheen.addColorStop(
        0.0,
        "rgba(210, 232, 248, " + (0.04 + intensity * 0.08) + ")",
      );
      sheen.addColorStop(
        0.55,
        "rgba(255, 255, 255, " + (0.02 + intensity * 0.04) + ")",
      );
      sheen.addColorStop(
        1.0,
        "rgba(160, 196, 220, " + (0.015 + intensity * 0.03) + ")",
      );
      g.fillStyle = sheen;
      g.fillRect(x, y, w, h);

      // Soft photographic sun catch - brightest near the hit, never a hard disk.
      const glow = g.createRadialGradient(cx, cy, 0, cx, cy, radius);
      const a = Math.min(1, intensity);
      glow.addColorStop(0.0, "rgba(255, 255, 255, " + (0.55 + a * 0.45) + ")");
      glow.addColorStop(0.18, "rgba(255, 252, 246, " + (0.38 + a * 0.4) + ")");
      glow.addColorStop(0.42, "rgba(236, 246, 255, " + (0.16 + a * 0.28) + ")");
      glow.addColorStop(0.72, "rgba(186, 220, 240, " + (0.04 + a * 0.1) + ")");
      glow.addColorStop(1.0, "rgba(255, 255, 255, 0.0)");
      g.fillStyle = glow;
      g.fillRect(x, y, w, h);

      // Tiny blown-out core only on the hottest panes.
      if (a > 0.62) {
        const coreR = Math.min(w, h) * 0.22 * a;
        const core = g.createRadialGradient(cx, cy, 0, cx, cy, coreR);
        core.addColorStop(0.0, "rgba(255, 255, 255, " + (0.7 + a * 0.3) + ")");
        core.addColorStop(
          0.45,
          "rgba(255, 255, 255, " + (0.25 + a * 0.25) + ")",
        );
        core.addColorStop(1.0, "rgba(255, 255, 255, 0.0)");
        g.fillStyle = core;
        g.fillRect(x, y, w, h);
      }
      g.restore();
    }
  }

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

function makeDistantTowerFacadeTexture(THREE, isDay, hasSunGlint) {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 512;
  const g = c.getContext("2d");

  // Deep dark obsidian/charcoal building silhouette base
  const baseGrad = g.createLinearGradient(0, 0, 0, 512);
  if (isDay) {
    baseGrad.addColorStop(0.0, "#1c222b");
    baseGrad.addColorStop(0.5, "#141920");
    baseGrad.addColorStop(1.0, "#0b0e13");
  } else {
    baseGrad.addColorStop(0.0, "#080f1a");
    baseGrad.addColorStop(0.5, "#0b1524");
    baseGrad.addColorStop(1.0, "#03060a");
  }
  g.fillStyle = baseGrad;
  g.fillRect(0, 0, 256, 512);

  // Window grid panes - faint, dark, semi-transparent feeling
  const cols = 6;
  const rows = 14;
  const cellW = 256 / cols;
  const cellH = 512 / rows;
  const padX = 3;
  const padY = 3;

  for (let r = 0; r < rows; r++) {
    for (let col = 0; col < cols; col++) {
      const px = col * cellW + padX;
      const py = r * cellH + padY;
      const pw = cellW - padX * 2;
      const ph = cellH - padY * 2;

      // Dark tinted glass with very faint subtle sheen
      if (isDay) {
        const paneGrad = g.createLinearGradient(px, py, px, py + ph);
        paneGrad.addColorStop(0.0, "rgba(70, 88, 108, 0.45)");
        paneGrad.addColorStop(0.6, "rgba(42, 54, 68, 0.35)");
        paneGrad.addColorStop(1.0, "rgba(22, 28, 36, 0.4)");
        g.fillStyle = paneGrad;
        g.fillRect(px, py, pw, ph);

        // Faint top edge reflection
        g.fillStyle = "rgba(255, 255, 255, 0.08)";
        g.fillRect(px, py, pw, Math.max(1, ph * 0.2));
      } else {
        // Nighttime NYC-style illuminated window grid
        // Deterministic pseudo-random seed per pane
        const seed = Math.sin(col * 12.9898 + r * 78.233) * 43758.5453;
        const rand = seed - Math.floor(seed);

        // ~35% of windows are illuminated in the city night
        const isLit = rand < 0.36;

        if (isLit) {
          // Window interior palette: warm amber, bright soft yellow, neon cyan-white, soft white
          // Scaled down alpha so the distant windows don't have overwhelming glare
          let lightColor;
          let lightAlpha = 0.4 + (rand % 0.2);
          if (rand < 0.16) {
            lightColor = "rgba(254, 215, 170, " + lightAlpha + ")"; // Warm cozy incandescent amber (#fed7aa)
          } else if (rand < 0.26) {
            lightColor = "rgba(254, 240, 138, " + lightAlpha + ")"; // Bright office yellow (#fef08a)
          } else if (rand < 0.32) {
            lightColor = "rgba(224, 242, 254, " + lightAlpha + ")"; // Cool modern LED / fluorescent white (#e0f2fe)
          } else {
            lightColor = "rgba(186, 230, 253, " + lightAlpha + ")"; // Soft sky cyan (#bae6fd)
          }

          g.fillStyle = lightColor;
          g.fillRect(px, py, pw, ph);

          // Subtle internal glow/curtain shade detail
          if (rand > 0.2) {
            g.fillStyle = "rgba(0, 0, 0, 0.35)";
            g.fillRect(px, py, pw, ph * 0.4); // Half-drawn blinds
          }
        } else {
          // Unlit office pane - deep dark blue/charcoal tinted glass with very faint reflection
          g.fillStyle = "#070e1a";
          g.fillRect(px, py, pw, ph);
        }
      }

      // Very soft, warm golden/yellowish sun glint (faded, subtle, not overpowering)
      if (isDay && hasSunGlint) {
        const u = (col + 0.5) / cols;
        const v = (r + 0.5) / rows;
        const du = u - 0.42;
        const dv = v - 0.38;
        const dist = Math.sqrt(du * du + dv * dv * 1.8);
        const falloff = Math.exp(-(dist * dist) / 0.08);
        if (falloff > 0.08) {
          const glow = g.createRadialGradient(
            px + pw * 0.45,
            py + ph * 0.4,
            0,
            px + pw * 0.45,
            py + ph * 0.4,
            Math.max(pw, ph) * 0.85,
          );
          // Warm golden/amber tone, soft opacity
          glow.addColorStop(
            0.0,
            "rgba(255, 245, 205, " + Math.min(0.65, 0.2 + falloff * 0.45) + ")",
          );
          glow.addColorStop(
            0.45,
            "rgba(255, 220, 150, " + falloff * 0.32 + ")",
          );
          glow.addColorStop(0.8, "rgba(240, 180, 100, " + falloff * 0.12 + ")");
          glow.addColorStop(1.0, "rgba(255, 220, 150, 0.0)");
          g.fillStyle = glow;
          g.fillRect(px, py, pw, ph);
        }
      }
    }
  }

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

function addFrontSunCatch(
  group,
  matFactory,
  w,
  h,
  y,
  z,
  cols,
  rows,
  hitU,
  hitV,
  strength,
) {
  const tex = makeFacadeSunCatchTexture(THREE, {
    cols,
    rows,
    width: w,
    height: h,
    hitU,
    hitV,
    strength,
  });
  const mat = matFactory(tex);
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(w * 0.995, h * 0.995),
    mat,
  );
  mesh.position.set(0, y, z);
  mesh.renderOrder = 2;
  group.add(mesh);
  return mesh;
}

export function buildBuilding(scene) {
  const isDay = isDayInIsrael();
  const buildingGroup = new THREE.Group();
  const cityEnvMap = makeCityEnvMap(THREE, isDay);

  // --- Materials ---
  // Deep premium obsidian tinted glass curtain (floors 1, 2, lobby)
  const glassMat = new THREE.MeshPhysicalMaterial({
    color: 0x070b12,
    metalness: 0.22,
    roughness: 0.08,
    transmission: 0.16,
    thickness: 1.0,
    transparent: true,
    opacity: 0.92,
    reflectivity: 0.72,
    clearcoat: 0.55,
    clearcoatRoughness: 0.08,
    ior: 1.5,
    envMap: cityEnvMap,
    envMapIntensity: isDay ? 0.85 : 0.55,
  });

  // Dedicated luxury architectural penthouse glass curtain (Floor 3)
  const phGlassMat = new THREE.MeshPhysicalMaterial({
    color: 0x070b12,
    metalness: 0.22,
    roughness: 0.08,
    transmission: 0.15,
    thickness: 1.0,
    transparent: true,
    opacity: 0.9,
    reflectivity: 0.72,
    clearcoat: 0.55,
    clearcoatRoughness: 0.08,
    ior: 1.5,
    envMap: cityEnvMap,
    envMapIntensity: isDay ? 0.85 : 0.55,
    depthWrite: false,
  });

  // Per-pane sun catch overlay - additive, but only where the texture has light.
  function makeSunCatchMat(tex) {
    return new THREE.MeshBasicMaterial({
      map: tex,
      transparent: true,
      opacity: isDay ? 0.82 : 0.28,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    });
  }

  const frameMat = new THREE.MeshStandardMaterial({
    color: 0x111111, // Pure matte jet black architectural structural framing & columns
    roughness: 0.35,
    metalness: 0.7,
  });

  const slabMat = new THREE.MeshStandardMaterial({
    color: 0x181818, // Pure sleek deep black/anthracite floor slabs & podium
    roughness: 0.45,
    metalness: 0.6,
  });

  // Architectural metal trim/accent (pure deep black metallic accents)
  const trimMat = new THREE.MeshStandardMaterial({
    color: 0x141414,
    roughness: 0.3,
    metalness: 0.8,
  });

  // Helper to build architectural facade window grid frames for each tier (positioned proudly outside the glass)
  function addTierWindowFrames(
    w,
    d,
    h,
    centerY,
    centerZ = 0,
    colsPerSide = 4,
    rowsPerSide = 3,
  ) {
    const offset = 0.08; // Protrude outside the glass surface so mullions and transoms are never behind glass
    const halfW = w / 2 + offset;
    const halfD = d / 2 + offset;
    const spanW = w + offset * 2;
    const spanD = d + offset * 2;

    const frameMeshMat = new THREE.MeshStandardMaterial({
      color: 0x141414, // Architectural pure jet black metallic window mullions & frames
      metalness: 0.8,
      roughness: 0.25,
    });

    // 4 Corner vertical structural pillars
    const cornerCol = new THREE.BoxGeometry(0.32, h, 0.32);
    for (const [cx, cz] of [
      [-halfW, -halfD],
      [halfW, -halfD],
      [-halfW, halfD],
      [halfW, halfD],
    ]) {
      const col = new THREE.Mesh(cornerCol, frameMeshMat);
      col.position.set(cx, centerY, cz + centerZ);
      buildingGroup.add(col);
    }

    // Vertical Mullions (extending out on North, South, East, West facades)
    const vertMullionNS = new THREE.BoxGeometry(0.18, h, 0.16);
    const vertMullionEW = new THREE.BoxGeometry(0.16, h, 0.18);

    for (let c = 1; c < colsPerSide; c++) {
      const u = -halfW + (spanW / colsPerSide) * c;
      const v = -halfD + (spanD / colsPerSide) * c;

      // North & South walls (Z = ±halfD + centerZ)
      const mN = new THREE.Mesh(vertMullionNS, frameMeshMat);
      mN.position.set(u, centerY, -halfD + centerZ);
      const mS = new THREE.Mesh(vertMullionNS, frameMeshMat);
      mS.position.set(u, centerY, halfD + centerZ);
      buildingGroup.add(mN, mS);

      // East & West walls (X = ±halfW)
      const mW = new THREE.Mesh(vertMullionEW, frameMeshMat);
      mW.position.set(-halfW, centerY, v + centerZ);
      const mE = new THREE.Mesh(vertMullionEW, frameMeshMat);
      mE.position.set(halfW, centerY, v + centerZ);
      buildingGroup.add(mW, mE);
    }

    // Horizontal Transom Divider Rails (creating distinct floor/window pane rows outside the glass)
    const railNS = new THREE.BoxGeometry(spanW + 0.1, 0.18, 0.16);
    const railEW = new THREE.BoxGeometry(0.16, 0.18, spanD + 0.1);

    for (let r = 1; r < rowsPerSide; r++) {
      const ry = centerY - h / 2 + (h / rowsPerSide) * r;

      const rN = new THREE.Mesh(railNS, frameMeshMat);
      rN.position.set(0, ry, -halfD + centerZ);
      const rS = new THREE.Mesh(railNS, frameMeshMat);
      rS.position.set(0, ry, halfD + centerZ);
      const rW = new THREE.Mesh(railEW, frameMeshMat);
      rW.position.set(-halfW, ry, centerZ);
      const rE = new THREE.Mesh(railEW, frameMeshMat);
      rE.position.set(halfW, ry, centerZ);
      buildingGroup.add(rN, rS, rW, rE);
    }
  }

  // --- 1. Street Podium / Base (Y: -6 to 10, Height 16) ---
  // Sub-ground foundation base below street level (Y: -8 to 0)
  const podiumGeo = new THREE.BoxGeometry(36, 8, 38);
  const podium = new THREE.Mesh(podiumGeo, frameMat);
  podium.position.set(0, -4, -6);
  buildingGroup.add(podium);

  // Grand Entrance Glass Lobby Atrium (front facing +Z) - Cathedral Height (H: 10.0, Ground Y: 0 to 10.0)
  const lobbyW = 28,
    lobbyH = 10,
    lobbyD = 20;
  const lobbyCenterY = 5.0,
    lobbyCenterZ = 4;
  const lobbyWallV = new THREE.BoxGeometry(0.12, lobbyH, lobbyD);
  const lobbyWalls = [
    new THREE.Mesh(lobbyWallV, glassMat), // West
    new THREE.Mesh(lobbyWallV, glassMat), // East
  ];
  lobbyWalls[0].position.set(-lobbyW / 2, lobbyCenterY, lobbyCenterZ);
  lobbyWalls[1].position.set(lobbyW / 2, lobbyCenterY, lobbyCenterZ);
  lobbyWalls.forEach((w) => buildingGroup.add(w));

  // Entrance Canopy (clean architectural metallic trim with LED undersill at Y = 8.2)
  const canopyGeo = new THREE.BoxGeometry(18, 0.5, 6);
  const canopy = new THREE.Mesh(canopyGeo, trimMat);
  canopy.position.set(0, 8.2, 14);
  buildingGroup.add(canopy);

  // --- 2. Floor 1: Server Farm / Hall (Y: 10 to 22, Height 12) ---
  // Sized to fully encapsulate the extensive server hall aisle & back racks
  const f1Width = 32;
  const f1Depth = 34;
  const f1Height = 12;
  const f1CenterY = 16;
  const f1CenterZ = -7;

  // Slab below F1 (top surface at Y = 10.0 separating Grand Lobby from Server Farm)
  const slab1 = new THREE.Mesh(
    new THREE.BoxGeometry(f1Width + 2, 0.6, f1Depth + 2),
    slabMat,
  );
  slab1.position.set(0, 9.7, f1CenterZ);
  buildingGroup.add(slab1);

  // Glass Curtain
  const f1Glass = new THREE.Mesh(
    new THREE.BoxGeometry(f1Width, f1Height, f1Depth),
    glassMat,
  );
  f1Glass.position.set(0, f1CenterY, f1CenterZ);
  buildingGroup.add(f1Glass);

  // Window Grid Panes for Floor 1 (Server Farm)
  addTierWindowFrames(f1Width, f1Depth, f1Height, f1CenterY, f1CenterZ, 7, 4);

  // Architectural trim band at F1 top
  const f1Band = new THREE.Mesh(
    new THREE.BoxGeometry(f1Width + 0.2, 0.25, f1Depth + 0.2),
    trimMat,
  );
  f1Band.position.set(0, 22, f1CenterZ);
  buildingGroup.add(f1Band);

  // --- 3. Floor 2: Boardroom Suite (Y: 22 to 34, Height 12) ---
  // Sized to fully encapsulate the deal room, long deal table, and presenter screen
  const f2Width = 30;
  const f2Depth = 30;
  const f2Height = 12;
  const f2CenterY = 28;
  const f2CenterZ = -8;

  // Slab below F2
  const slab2 = new THREE.Mesh(
    new THREE.BoxGeometry(f2Width + 2, 0.6, f2Depth + 2),
    slabMat,
  );
  slab2.position.set(0, 22, f2CenterZ);
  buildingGroup.add(slab2);

  // Glass Curtain - OPEN glass shell for Floor 2 (facades only, no top/bottom faces).
  // Prevents internal facade glass from slicing through the boardroom interior.
  const f2WallH = new THREE.BoxGeometry(f2Width, f2Height, 0.15);
  const f2WallV = new THREE.BoxGeometry(0.15, f2Height, f2Depth);
  const f2Walls = [
    new THREE.Mesh(f2WallH, glassMat), // North
    new THREE.Mesh(f2WallH, glassMat), // South
    new THREE.Mesh(f2WallV, glassMat), // West
    new THREE.Mesh(f2WallV, glassMat), // East
  ];
  f2Walls[0].position.set(0, f2CenterY, f2CenterZ - f2Depth / 2);
  f2Walls[1].position.set(0, f2CenterY, f2CenterZ + f2Depth / 2);
  f2Walls[2].position.set(-f2Width / 2, f2CenterY, f2CenterZ);
  f2Walls[3].position.set(f2Width / 2, f2CenterY, f2CenterZ);
  f2Walls.forEach((w) => buildingGroup.add(w));

  // Window Grid Panes for Floor 2 (Boardroom)
  addTierWindowFrames(f2Width, f2Depth, f2Height, f2CenterY, f2CenterZ, 6, 3);

  // Architectural trim band at F2 top
  const f2Band = new THREE.Mesh(
    new THREE.BoxGeometry(f2Width + 0.2, 0.25, f2Depth + 0.2),
    trimMat,
  );
  f2Band.position.set(0, 34, f2CenterZ);
  buildingGroup.add(f2Band);

  // --- 4. Floor 3: Penthouse / Executive Suite (Y: 34 to 46, Height 12) ---
  const phWidth = 15;
  const phDepth = 15;
  const phHeight = 12;
  const phCenterY = 40;

  // Slab below Penthouse
  const slab3 = new THREE.Mesh(new THREE.BoxGeometry(16, 0.6, 16), slabMat);
  slab3.position.set(0, 34, 0);
  buildingGroup.add(slab3);

  // Glass Curtain - OPEN glass shell (4 facades only, NO bottom/top faces).
  // A closed box had hidden glass faces directly below the penthouse floor
  // and inside the roof slab, which caused white/black render flashes on the
  // executive floor. An open shell keeps the interior clean and stable.
  const wallGeoH = new THREE.BoxGeometry(phWidth, phHeight, 0.15); // N/S walls
  const wallGeoV = new THREE.BoxGeometry(0.15, phHeight, phDepth); // E/W walls
  const phWalls = [
    new THREE.Mesh(wallGeoH, phGlassMat),
    new THREE.Mesh(wallGeoH, phGlassMat),
    new THREE.Mesh(wallGeoV, phGlassMat),
    new THREE.Mesh(wallGeoV, phGlassMat),
  ];
  phWalls[0].position.set(0, phCenterY, -phDepth / 2); // North
  phWalls[1].position.set(0, phCenterY, phDepth / 2); // South
  phWalls[2].position.set(-phWidth / 2, phCenterY, 0); // West
  phWalls[3].position.set(phWidth / 2, phCenterY, 0); // East
  phWalls.forEach((w) => buildingGroup.add(w));

  // Window Grid Panes for Penthouse Floor
  addTierWindowFrames(phWidth, phDepth, phHeight, phCenterY, 0, 4, 3);

  // Perimeter Roof Balustrade / Ledge (Y: 46)
  const roofSlab = new THREE.Mesh(
    new THREE.BoxGeometry(15.5, 0.8, 15.5),
    slabMat,
  );
  roofSlab.position.set(0, 46, 0);
  buildingGroup.add(roofSlab);

  // Front-facade sun catch: light lives inside the window grid so the
  // mullions stay dark and break the highlight like real curtain-wall glass.
  addFrontSunCatch(
    buildingGroup,
    makeSunCatchMat,
    f1Width,
    f1Height,
    f1CenterY,
    f1CenterZ + f1Depth / 2 + 0.14,
    7,
    4,
    0.36,
    0.42,
    1.0,
  );
  addFrontSunCatch(
    buildingGroup,
    makeSunCatchMat,
    f2Width,
    f2Height,
    f2CenterY,
    f2CenterZ + f2Depth / 2 + 0.14,
    6,
    3,
    0.4,
    0.48,
    0.72,
  );
  addFrontSunCatch(
    buildingGroup,
    makeSunCatchMat,
    phWidth,
    phHeight,
    phCenterY,
    phDepth / 2 + 0.14,
    4,
    3,
    0.44,
    0.55,
    0.42,
  );

  // Architectural roof trim ledge
  const roofTrim = new THREE.Mesh(
    new THREE.BoxGeometry(15.6, 0.25, 15.6),
    trimMat,
  );
  roofTrim.position.set(0, 46.4, 0);
  buildingGroup.add(roofTrim);

  // --- 5. Spire & Architectural Crown (Y: 46 to 66) ---
  const crownGeo = new THREE.ConeGeometry(5.5, 8, 4);
  const crown = new THREE.Mesh(crownGeo, frameMat);
  crown.position.set(0, 50.5, 0);
  crown.rotation.y = Math.PI / 4;
  buildingGroup.add(crown);

  // Base crown trim
  const crownTrim = new THREE.Mesh(
    new THREE.BoxGeometry(6.2, 0.25, 6.2),
    trimMat,
  );
  crownTrim.position.set(0, 47, 0);
  crownTrim.rotation.y = Math.PI / 4;
  buildingGroup.add(crownTrim);

  const spireGeo = new THREE.CylinderGeometry(0.12, 0.45, 14, 8);
  const spire = new THREE.Mesh(spireGeo, frameMat);
  spire.position.set(0, 59, 0);
  buildingGroup.add(spire);

  const beaconGeo = new THREE.SphereGeometry(0.4, 12, 12);
  const beaconMat = new THREE.MeshBasicMaterial({
    color: 0xef4444,
    toneMapped: false,
  });
  const beacon = new THREE.Mesh(beaconGeo, beaconMat);
  beacon.position.set(0, 66, 0);
  buildingGroup.add(beacon);

  // --- 6. Vertical Corner Mullions & Structural Columns ---
  const colGeo = new THREE.BoxGeometry(0.35, 50, 0.35);
  const halfW = 7.5;
  const halfD = 7.5;
  for (const [cx, cz] of [
    [-halfW, -halfD],
    [halfW, -halfD],
    [-halfW, halfD],
    [halfW, halfD],
  ]) {
    const col = new THREE.Mesh(colGeo, frameMat);
    col.position.set(cx, 25, cz);
    buildingGroup.add(col);
  }

  // --- 7. Distant City Skyline Silhouettes with Glass Panes & Sun Reflections ---
  const cityGroup = new THREE.Group();

  const texDayGlint = makeDistantTowerFacadeTexture(THREE, true, true);
  const texDayPlain = makeDistantTowerFacadeTexture(THREE, true, false);
  const texNight1 = makeDistantTowerFacadeTexture(THREE, false, false);
  const texNight2 = makeDistantTowerFacadeTexture(THREE, false, false);

  const matDayGlint = new THREE.MeshStandardMaterial({
    map: texDayGlint,
    roughness: 0.6,
    metalness: 0.15,
    envMap: cityEnvMap,
    envMapIntensity: 0.25,
  });
  const matDayPlain = new THREE.MeshStandardMaterial({
    map: texDayPlain,
    roughness: 0.7,
    metalness: 0.1,
    envMap: cityEnvMap,
    envMapIntensity: 0.15,
  });
  const matNight1 = new THREE.MeshStandardMaterial({
    map: texNight1,
    roughness: 0.85,
    metalness: 0.1,
    emissiveMap: texNight1,
    emissive: 0xffffff,
    emissiveIntensity: 0.38,
  });
  const matNight2 = new THREE.MeshStandardMaterial({
    map: texNight2,
    roughness: 0.85,
    metalness: 0.1,
    emissiveMap: texNight2,
    emissive: 0xffffff,
    emissiveIntensity: 0.32,
  });

  const cityRadii = [45, 60, 80];
  const countPerRadius = [8, 12, 16];

  cityRadii.forEach((r, idx) => {
    const count = countPerRadius[idx];
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.2;
      const x = Math.cos(angle) * r;
      const z = Math.sin(angle) * r;
      const h = 18 + Math.random() * 45;
      const w = 6 + Math.random() * 8;
      const d = 6 + Math.random() * 8;

      let chosenMat;
      if (!isDay) {
        chosenMat = i % 2 === 0 ? matNight1 : matNight2;
      } else {
        // ~45% of background towers facing the camera quadrant get the crisp sun glint
        const facingSouth = z > -15;
        chosenMat =
          facingSouth && Math.random() > 0.4 ? matDayGlint : matDayPlain;
      }

      const bgMesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), chosenMat);
      bgMesh.position.set(x, h / 2 - 8, z);
      cityGroup.add(bgMesh);
    }
  });
  buildingGroup.add(cityGroup);

  scene.add(buildingGroup);

  return {
    buildingGroup,
    beacon,
  };
}
